import React, { useState, useEffect } from "react";
import {
  DatePicker,
  Table,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Tooltip,
  Modal,
  InputNumber,
  Input,
  Button,
  Descriptions,
  Alert,
  Tabs,
  message,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useFetchKpiReport, useSaveAttitude } from "../hooks/useFetch";

const { TextArea } = Input;

const SHIFT_NAME = { 1: "ทั้งวัน", 2: "เช้า", 3: "ค่ำ", 4: "บ่าย" };
// แท็บตามกะ — กะที่ไม่รู้จักไปอยู่ "ทั้งวัน" (backend ก็คิดแบบทั้งวันให้)
const SHIFT_TABS = ["all", "2", "4", "3", "1"];
const tabOf = (shift) => (["2", "3", "4"].includes(String(shift)) ? String(shift) : "1");

// สรุปของแท็บ — avg คิดเฉพาะคนที่มีสิทธิ์โบนัส (เหมือน summary เดิมของทั้งบริษัท)
const summarize = (rows) => {
  const eligible = rows.filter((r) => r.eligible);
  return {
    total: rows.length,
    eligible: eligible.length,
    avgScore: eligible.length
      ? eligible.reduce((a, r) => a + r.scores.total, 0) / eligible.length
      : null,
    totalBonus: eligible.reduce((a, r) => a + r.bonus, 0),
    pendingAttitude: rows.filter((r) => !r.attitudeRated).length,
  };
};

// สีเกรด — ไล่จากเขียว (ดี) ไปแดง (ต้องปรับปรุง)
const GRADE_COLOR = {
  S: "#237804", "A+": "#389e0d", A: "#52c41a", "B+": "#a0d911",
  B: "#fadb14", "C+": "#fa8c16", C: "#fa541c", "D+": "#f5222d",
  D: "#cf1322", F: "#5c0011",
};

// คะแนนติดลบได้ (จิตพิสัย -1) → คุมแถบ progress ไม่ให้หลุดกรอบ
const clampPct = (score) => Math.max(0, Math.min(100, (score / 10) * 100));

const fmt = (v, d = 2) =>
  v == null ? "-" : Number(v).toLocaleString("th-TH", { maximumFractionDigits: d });


const bandLine = (label, value, st, max, score, d = 0) =>
  st ? (
    <div>
      {label}: {max} × ({fmt(value, d)} − {fmt(st.zero, d)}) ÷ ({fmt(st.full, d)} − {fmt(st.zero, d)}) = <b>{fmt(score)}</b>
      <span style={{ fontSize: 12, color: "#8c8c8c" }}>
        {" "}(เฉลี่ย {fmt(st.mean, d)} · SD {fmt(st.sd, d)} → เต็ม ≥ {fmt(st.full, d)} · 0 ≤ {fmt(st.zero, d)})
      </span>
    </div>
  ) : (
    <div>
      {label}: <b>{fmt(score)}</b>
    </div>
  );

// อธิบายว่าความเร็ว/ปริมาณของคนนี้ไปเทียบกับใคร
//   กะเช้า/ค่ำ = เทียบกับคนในกะเดียวกันทั้งเดือน
//   กะบ่าย/ทั้งวัน = แบ่งเคสตามเวลาที่เข้ามา แต่ละช่วงเทียบกับกะเช้า/ค่ำ ช่วงเวลาเดียวกัน แล้วถ่วงตามชั่วโมงที่ทำ
const CompareDetail = ({ detail }) => {
  const c = detail.comparison;
  if (!c) {
    return (
      <>
        ความเร็ว <b>{fmt(detail.scores.speed)}</b> · ปริมาณ <b>{fmt(detail.scores.volume)}</b>
      </>
    );
  }
  if (c.mode === "shift") {
    return (
      <>
        <div style={{ marginBottom: 4 }}>
          เทียบกับคนกะ{SHIFT_NAME[c.shift]}ที่เข้าเกณฑ์ {c.n} คน (เคสทั้งเดือน)
        </div>
        {bandLine("ความเร็ว", detail.metrics.speed, c.speed, 2, detail.scores.speed, 2)}
        {bandLine("ปริมาณ (workload รวม)", detail.raw.workload, c.workload, 1, detail.scores.volume)}
      </>
    );
  }
  return (
    <>
      <div style={{ marginBottom: 4 }}>
        กะ{SHIFT_NAME[c.shift] || c.shift} ทำงานคร่อมสองกะ — แบ่งเคสตามเวลาที่เคสเข้ามา
        แต่ละช่วงเทียบกับคนกะเช้า/ค่ำ เฉพาะเคสที่เข้ามาช่วงเวลาเดียวกัน
      </div>
      {c.segments.map((g) => (
        <div key={g.base} style={{ borderLeft: "3px solid #d9d9d9", paddingLeft: 8, marginBottom: 6 }}>
          <div style={{ fontWeight: 600 }}>
            ช่วง {g.hours} — เทียบกะ{SHIFT_NAME[g.base]} {g.n} คน · ทำ {fmt(g.workHours)} ชม. (น้ำหนัก {fmt(g.weightPct, 0)}%)
          </div>
          {bandLine("ความเร็ว", g.speed, g.refSpeed, 2, g.scoreSpeed, 2)}
          {bandLine("ปริมาณ", g.workload, g.refWorkload, 1, g.scoreVolume)}
        </div>
      ))}
      <div>
        รวมถ่วงตามชั่วโมง → ความเร็ว <b>{fmt(detail.scores.speed)}</b> · ปริมาณ <b>{fmt(detail.scores.volume)}</b>
      </div>
    </>
  );
};

const KpiReportPage = () => {
  const [month, setMonth] = useState(dayjs().subtract(1, "month"));
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null); // แถวที่กดดูรายละเอียด
  const [rating, setRating] = useState(null); // แถวที่กำลังให้คะแนนจิตพิสัย
  const [attScore, setAttScore] = useState(1);
  const [attNote, setAttNote] = useState("");
  const [shiftTab, setShiftTab] = useState("all"); // แท็บกะที่เลือก

  const { mutateAsync: fetchKpi, isLoading } = useFetchKpiReport();
  const { mutateAsync: saveAttitude, isLoading: isSaving } = useSaveAttitude();

  const load = async (m = month) => {
    try {
      const res = await fetchKpi({ month: m.format("YYYY-MM") });
      setData(res);
    } catch (e) {
      message.error(e?.response?.data?.message || "ดึงรายงาน KPI ไม่สำเร็จ");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRating = (r) => {
    setRating(r);
    setAttScore(r.scores.attitude ?? 1);
    setAttNote(r.scores.attitudeNote || "");
  };

  const submitRating = async () => {
    try {
      await saveAttitude({
        month: month.format("YYYY-MM"),
        userId: rating.userId,
        score: attScore,
        note: attNote,
      });
      message.success("บันทึกคะแนนจิตพิสัยสำเร็จ");
      setRating(null);
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const S = data?.standard;
  // แท็บกะ: แท็บ all แค่รวมทุกคนมาแสดง คะแนนของแต่ละคนเป็นชุดเดียวกันทุกแท็บ (backend เทียบในกะให้แล้ว)
  const allRows = data?.rows || [];
  const viewRows = shiftTab === "all" ? allRows : allRows.filter((r) => tabOf(r.shift) === shiftTab);
  const sum = summarize(viewRows);
  const tabLabel = (key) => {
    const n = key === "all" ? allRows.length : allRows.filter((r) => tabOf(r.shift) === key).length;
    const name = key === "all" ? "ทั้งหมด" : SHIFT_NAME[key];
    const hours = key === "all" ? "" : data?.shifts?.[key]?.hours;
    return `${name}${hours ? ` (${hours})` : ""} · ${n} คน`;
  };
  const BAND_HINT = `เทียบกับคนที่ทำงานช่วงเวลาเดียวกัน · ≥ ค่าเฉลี่ย + ${S?.sdFull ?? 1} SD = เต็ม · ≤ ค่าเฉลี่ย − ${S?.sdZero ?? 1.5} SD = 0 · ระหว่างนั้นตามสัดส่วน`;

  const columns = [
    {
      title: "อันดับ",
      key: "rank",
      align: "center",
      width: 70,
      render: (_, __, i) => i + 1,
    },
    {
      title: "พนักงาน",
      key: "userName",
      width: 150,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.userName}</div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            {SHIFT_NAME[r.shift] || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "เกรด",
      key: "grade",
      align: "center",
      width: 90,
      render: (_, r) => (
        <div>
          <Tag
            color={GRADE_COLOR[r.grade]}
            style={{ fontSize: 16, fontWeight: 700, padding: "2px 12px" }}
          >
            {r.grade}
          </Tag>
          {r.gradeMoved && (
            // จิตพิสัยดันขึ้น/ดึงลงจากเกรดฐาน — บอกให้เห็นว่าไม่ได้มาจากข้อมูลล้วน
            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
              ฐาน {r.baseGrade}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "คะแนน",
      key: "total",
      align: "center",
      width: 165,
      sorter: (a, b) => a.scores.total - b.scores.total,
      defaultSortOrder: "descend",
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {fmt(r.scores.total)}
          </div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            ฐาน {fmt(r.scores.base)}/9
            {r.scores.attitude != null && (
              <span style={{ color: r.scores.attitude < 0 ? "#cf1322" : "#237804" }}>
                {" "}{r.scores.attitude >= 0 ? "+" : ""}{fmt(r.scores.attitude)}
              </span>
            )}
          </div>
          <Progress
            percent={clampPct(r.scores.total)}
            showInfo={false}
            size="small"
            strokeColor={GRADE_COLOR[r.grade]}
          />
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={`workload ต่อชั่วโมงที่ลงมือทำจริง · ${BAND_HINT}`}>
          ความเร็ว /2
        </Tooltip>
      ),
      key: "speed",
      align: "center",
      width: 120,
      sorter: (a, b) => a.scores.speed - b.scores.speed,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{fmt(r.scores.speed)}</div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            {fmt(r.metrics.speed, 0)} wl/ชม.
          </div>
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={`workload รวมทั้งเดือน · ${BAND_HINT}`}>
          ปริมาณ /1
        </Tooltip>
      ),
      key: "volume",
      align: "center",
      width: 110,
      sorter: (a, b) => a.scores.volume - b.scores.volume,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{fmt(r.scores.volume)}</div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            {fmt(r.raw.workload, 0)} wl
          </div>
        </div>
      ),
    },
    {
      title: (
        <Tooltip
          title={`อยู่ในกรอบมั้ย — โควตา ${S?.errQuotaPerCases} เคสต่อ 1 ครั้ง ปัดลงเป็นจำนวนเต็ม · ผิดในโควตาไม่หัก · เกินโควตา 1 เท่า = 0 คะแนน · ทำไม่ถึง ${S?.errQuotaPerCases} เคส (โควตา 0) ผิดครั้งเดียวก็ 0`}
        >
          โควตา /1
        </Tooltip>
      ),
      key: "errorQuota",
      align: "center",
      width: 125,
      sorter: (a, b) => a.scores.errorQuota - b.scores.errorQuota,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {r.raw.errorCount === r.metrics.errorQuota && r.raw.errorCount !== 0
              ? fmt(0.5)
              : fmt(r.scores.errorQuota)}
          </div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            ผิด {r.raw.errorCount} / โควตา {r.metrics.errorQuota}
            {r.metrics.errorExcess > 0 && (
              <div style={{ color: "#cf1322" }}>เกิน {fmt(r.metrics.errorExcess)}</div>
            )}
          </div>
        </div>
   
      ),
    },
    {
      title: (
        <Tooltip
          title="งานสะอาดแค่ไหน — ใช้โควตาไปกี่ % (โควตาแบบไม่ปัด) · 0% = เต็ม 2 · 100% (ผิดพอดีโควตา) = 1 · 200% = 0"
        >
          อัตราผิด /2
        </Tooltip>
      ),
      key: "errorRate",
      align: "center",
      width: 135,
      sorter: (a, b) => a.scores.errorRate - b.scores.errorRate,
      render: (_, r) => {
        const pct = r.metrics.errorQuotaUsedPct;
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{fmt(r.scores.errorRate)}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              ใช้โควตา{" "}
              <span style={{ color: pct == null || pct > 100 ? "#cf1322" : undefined }}>
                {pct == null ? "เกินทั้งหมด" : `${fmt(pct, 1)}%`}
              </span>
              <div>จาก {fmt(r.metrics.errorQuotaExact)} ครั้ง</div>
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <Tooltip
          title={`เวลาที่เคสรอก่อนกด Start (หักเวลาที่ทำเคสอื่นอยู่แล้ว) · เต็มเมื่อเฉลี่ย ≤ ${fmt((S?.idleFull ?? 0) * 60, 0)} วิ · แล้วหักแต้มเคสที่รับช้าเกิน ${S?.slowFromSeconds ?? 60} วิ ÷ ${S?.slowDivisor ?? "-"}`}
        >
          รับผิดชอบ /3
        </Tooltip>
      ),
      key: "idle",
      align: "center",
      width: 130,
      sorter: (a, b) => a.scores.idle - b.scores.idle,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{fmt(r.scores.idle)}</div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            รอ {fmt(r.metrics.idleAvg * 60, 0)} วินาที/เคส
          </div>
          {r.scores.idlePenalty > 0 && (
            <div style={{ fontSize: 12, color: "#cf1322" }}>
              เคสช้า −{fmt(r.scores.idlePenalty)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "จิตพิสัย /1",
      key: "attitude",
      align: "center",
      width: 120,
      render: (_, r) => (
        <Button
          size="small"
          type={r.attitudeRated ? "default" : "primary"}
          icon={<EditOutlined />}
          onClick={() => openRating(r)}
        >
          {r.attitudeRated ? fmt(r.scores.attitude) : "ให้คะแนน"}
        </Button>
      ),
    },
    {
      title: "โบนัส",
      key: "bonus",
      align: "right",
      width: 130,
      sorter: (a, b) => a.bonus - b.bonus,
      render: (_, r) =>
        r.eligible ? (
          <span style={{ fontWeight: 700, color: "#237804" }}>
            {fmt(r.bonus, 0)} ฿
          </span>
        ) : (
          <Tooltip title={r.ineligibleReason}>
            <Tag color="default">ไม่เข้าเกณฑ์</Tag>
          </Tooltip>
        ),
    },
    {
      title: "รายละเอียด",
      key: "detail",
      align: "center",
      width: 110,
      fixed: "right",
      render: (_, r) => (
        <Button size="small" onClick={() => setDetail(r)}>
          ดูวิธีคิด
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh", marginLeft: 16 }}>
      <h1 style={{ margin: "0 0 20px", fontSize: "2rem", fontWeight: "bold", color: "#212121" }}>
        KPI พนักงาน (รายเดือน)
      </h1>

      <div style={{ background: "#fff", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div className="flex gap-3 items-center flex-wrap">
          <DatePicker
            picker="month"
            value={month}
            onChange={(m) => {
              if (!m) return;
              setMonth(m);
              load(m);
            }}
            format="MM/YYYY"
            allowClear={false}
            disabledDate={(d) => d.isAfter(dayjs(), "month")}
          />
          <Button type="primary" loading={isLoading} onClick={() => load()}>
            ค้นหา
          </Button>
          <span style={{ color: "#8c8c8c", fontSize: 13 }}>
            เฉพาะพนักงาน Panel(Output) — ฝั่ง Admin(Input) ยังคำนวณไม่ได้
          </span>
        </div>
      </div>

      <Tabs
        activeKey={shiftTab}
        onChange={setShiftTab}
        items={SHIFT_TABS.map((k) => ({ key: k, label: tabLabel(k) }))}
        style={{ marginBottom: 8 }}
      />

      {sum.pendingAttitude > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`ยังไม่ได้ให้คะแนนจิตพิสัย ${sum.pendingAttitude} คน`}
          description="คะแนนรวมและโบนัสจะยังไม่ครบจนกว่าหัวหน้าจะประเมินครบทุกคน (ตอนนี้คิดจิตพิสัยเป็น 0)"
        />
      )}

      {data?.grades && (
        <div style={{ background: "#fff", borderRadius: 10, padding: "10px 16px", marginBottom: 16,
          display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontWeight: 600, color: "#595959" }}>ตารางเกรด</span>
          {data.grades.map((g) => (
            <Tag key={g.g} color={GRADE_COLOR[g.g]} style={{ fontWeight: 600 }}>
              {g.g} {g.min == null ? "" : `≥${g.min}`} · {fmt(g.money, 0)}฿
            </Tag>
          ))}
        </div>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card><Statistic title="พนักงาน" value={sum.total} suffix="คน" /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="เข้าเกณฑ์รับโบนัส" value={sum.eligible} suffix="คน" />
            <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
              ต้องทำงานจริง ≥ {S?.gateHoursPerDay} ชม./วัน
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={`คะแนนเฉลี่ย${shiftTab === "all" ? "" : `กะ${SHIFT_NAME[shiftTab]}`}`}
              value={sum.avgScore ?? "-"}
              suffix={sum.avgScore == null ? "" : "/10"}
              precision={2}
            />
            <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
              เฉลี่ยจาก {sum.eligible} คนที่เข้าเกณฑ์
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="โบนัสรวม"
              value={sum.totalBonus}
              suffix="฿"
              valueStyle={{ color: "#237804" }}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ background: "#fff", borderRadius: 10, padding: 16 }}>
        <Table
          rowKey="userId"
          loading={isLoading}
          dataSource={viewRows}
          columns={columns}
          pagination={false}
          bordered
          size="small"
          scroll={{ x: "max-content" }}
          rowClassName={(r) => (r.eligible ? "" : "kpi-ineligible")}
        />
      </div>

      {/* ===== รายละเอียดวิธีคิดรายคน ===== */}
      <Modal
        title={detail ? `วิธีคิดคะแนน — ${detail.userName}` : ""}
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={820}
      >
        {detail && S && (
          <>
            <Descriptions title="1) ตัวเลขดิบที่เก็บได้" bordered size="small" column={2}>
              <Descriptions.Item label="เคสที่ปิด">{fmt(detail.raw.caseCount, 0)} เคส</Descriptions.Item>
              <Descriptions.Item label="workload รวม">{fmt(detail.raw.workload)}</Descriptions.Item>
              <Descriptions.Item label="เวลาที่ลงมือทำจริง">{fmt(detail.raw.workHours)} ชม.</Descriptions.Item>
              <Descriptions.Item label="วันที่มาทำงาน">{detail.raw.workDays} วัน</Descriptions.Item>
              <Descriptions.Item label="ถูกตัดสินว่าผิด">{detail.raw.errorCount} ครั้ง</Descriptions.Item>
              <Descriptions.Item label="เวลารอรวม">{fmt(detail.raw.idleMinutes, 0)} นาที ({fmt(detail.raw.idleCases, 0)} เคส)</Descriptions.Item>
            </Descriptions>

            <Descriptions title="2) แปลงเป็นค่าที่ใช้คิด" bordered size="small" column={2} style={{ marginTop: 16 }}>
              <Descriptions.Item label="ความเร็ว">
                {fmt(detail.raw.workload)} ÷ {fmt(detail.raw.workHours)} ชม. = <b>{fmt(detail.metrics.speed)}</b> wl/ชม.
              </Descriptions.Item>
              <Descriptions.Item label="ปริมาณ">
                {fmt(detail.raw.workHours)} ÷ {detail.raw.workDays} วัน = <b>{fmt(detail.metrics.hoursPerDay)}</b> ชม./วัน
              </Descriptions.Item>
              <Descriptions.Item label="โควตา (ปัดลง) — ใช้กับเกณฑ์ 1 คะแนน">
                {fmt(detail.raw.caseCount, 0)} ÷ {S.errQuotaPerCases} = {fmt(detail.metrics.errorQuotaExact)} →
                ปัดลงเหลือโควตา <b>{detail.metrics.errorQuota}</b> ครั้ง
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  ผิดจริง {detail.raw.errorCount} ครั้ง →{" "}
                  {detail.metrics.errorExcess > 0 ? (
                    <b style={{ color: "#cf1322" }}>เกินโควตา {fmt(detail.metrics.errorExcess)}</b>
                  ) : (
                    <b style={{ color: "#237804" }}>อยู่ในโควตา ไม่หัก</b>
                  )}
                  {detail.metrics.casesPerError && ` · ผิด 1 ครั้งต่อ ${fmt(detail.metrics.casesPerError, 0)} เคส`}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ใช้โควตาไปกี่ % (ไม่ปัด) — ใช้กับเกณฑ์ 2 คะแนน">
                {detail.raw.errorCount} ÷ {fmt(detail.metrics.errorQuotaExact)} ={" "}
                <b style={{ color: (detail.metrics.errorQuotaUsedPct ?? 999) > 100 ? "#cf1322" : "#237804" }}>
                  {detail.metrics.errorQuotaUsedPct == null
                    ? "เกินโควตาทั้งหมด"
                    : `${fmt(detail.metrics.errorQuotaUsedPct, 1)}%`}
                </b>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  100% = ผิดพอดีโควตา · เกิน 200% = 0 คะแนน
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="เวลารอเฉลี่ย">
                {fmt(detail.raw.idleMinutes, 0)} ÷ {fmt(detail.raw.idleCases, 0)} เคส = <b>{fmt(detail.metrics.idleAvg)}</b> นาที/เคส
              </Descriptions.Item>
              <Descriptions.Item label={`เคสที่รับช้าเกิน ${S.slowFromSeconds ?? 60} วิ`} span={2}>
                {fmt(detail.metrics.slowCases ?? 0, 0)} เคส → แต้มเคสช้า <b>{fmt(detail.metrics.slowPoints ?? 0, 0)}</b>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  ช่วงละ {S.slowStepSeconds ?? 30} วิ: 61–90 วิ = 1 แต้ม · 91–120 = 2 · 121–150 = 3 · … (ไม่นับเคสที่เกิน 1 ชม.)
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="เวลาต่อเคส" span={2}>
                {fmt(detail.metrics.secondsPerCase, 0)} วินาที/เคส
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="3) คิดเป็นคะแนน" bordered size="small" column={1} style={{ marginTop: 16 }}>
              <Descriptions.Item label="ความเร็ว (เต็ม 2) + ปริมาณ (เต็ม 1) — เทียบกับคนที่ทำงานช่วงเวลาเดียวกัน">
                <CompareDetail detail={detail} />
              </Descriptions.Item>
              <Descriptions.Item label="ผิดพลาด 3.1 — โควตา (เต็ม 1)">
                {detail.metrics.errorExcess > 0
                  ? `1 × (1 − ${fmt(detail.metrics.errorExcessRate, 3)} ÷ ${S.errExcessZero}) = `
                  : "อยู่ในโควตา → "}
                <b>{fmt(detail.scores.errorQuota)}</b>
              </Descriptions.Item>
              <Descriptions.Item label={`ผิดพลาด 3.2 — อัตราใช้โควตา (เต็ม 2 · ใช้เกิน ${S.errUsedZero * 100}% = 0)`}>
                {detail.metrics.errorQuotaUsedPct == null
                  ? "ใช้โควตาเกินทั้งหมด → "
                  : `2 × (1 − ${fmt(detail.metrics.errorQuotaUsedPct / 100, 3)} ÷ ${S.errUsedZero}) = `}
                <b>{fmt(detail.scores.errorRate)}</b>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  รวมเกณฑ์ความผิดพลาด {fmt(detail.scores.errorQuota)} + {fmt(detail.scores.errorRate)} ={" "}
                  <b>{fmt(detail.scores.error)}</b> / 3
                </div>
              </Descriptions.Item>
              <Descriptions.Item
                label={`รับผิดชอบ (เต็ม 3 · เต็มที่เฉลี่ย ≤${fmt(S.idleFull * 60, 0)} วิ · 0 ที่ ≥${fmt(S.idleZero * 60, 0)} วิ · หักแต้มเคสช้า ÷ ${S.slowDivisor ?? "-"})`}
              >
                คะแนนฐาน 3 × ({S.idleZero} − {fmt(detail.metrics.idleAvg)}) ÷ ({S.idleZero}−{S.idleFull}) ={" "}
                {fmt(detail.scores.idleBase ?? detail.scores.idle)}
                {detail.scores.idlePenalty != null && (
                  <>
                    {" "}− แต้มเคสช้า {fmt(detail.metrics.slowPoints, 0)} ÷ {S.slowDivisor} ({fmt(detail.scores.idlePenalty)})
                  </>
                )}
                {" = "}<b>{fmt(detail.scores.idle)}</b>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>หักได้จนเหลือ 0 ไม่ติดลบ</div>
              </Descriptions.Item>
              <Descriptions.Item label={`จิตพิสัย (${S.attitudeMin} ถึง +${S.attitudeMax} · หัวหน้าให้)`}>
                {detail.attitudeRated ? (
                  <>
                    <b>{fmt(detail.scores.attitude)}</b>
                    {detail.scores.attitudeNote && (
                      <div style={{ fontSize: 12, color: "#8c8c8c" }}>{detail.scores.attitudeNote}</div>
                    )}
                  </>
                ) : (
                  <span style={{ color: "#fa8c16" }}>ยังไม่ประเมิน (คิดเป็น 0)</span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="รวม">
                <span style={{ fontSize: 18, fontWeight: 700 }}>
                  ฐาน {fmt(detail.scores.base)}/9
                  {detail.scores.attitude != null && ` ${detail.scores.attitude >= 0 ? "+" : ""}${fmt(detail.scores.attitude)}`}
                  {" = "}{fmt(detail.scores.total)}
                </span>
                <Tag color={GRADE_COLOR[detail.grade]} style={{ marginLeft: 8, fontWeight: 700 }}>
                  {detail.grade}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="4) คิดเป็นเงิน" bordered size="small" column={1} style={{ marginTop: 16 }}>
              <Descriptions.Item label="ผ่านประตูขั้นต่ำ">
                {detail.eligible ? (
                  <Tag color="green">ผ่าน — ทำงานจริง {fmt(detail.metrics.hoursPerDay)} ชม./วัน (เกณฑ์ ≥ {S.gateHoursPerDay})</Tag>
                ) : (
                  <Tag color="red">ไม่ผ่าน — {detail.ineligibleReason}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="โบนัสตามเกรด">
                {detail.eligible ? (
                  <>
                    เกรด <b>{detail.grade}</b> ={" "}
                    <b style={{ color: "#237804", fontSize: 16 }}>{fmt(detail.bonus, 0)} ฿</b>
                    {detail.gradeMoved && (
                      <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                        จิตพิสัยขยับจากเกรดฐาน {detail.baseGrade} → {detail.grade}
                      </div>
                    )}
                  </>
                ) : (
                  <b>0 ฿ (ไม่เข้าเกณฑ์)</b>
                )}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>

      {/* ===== ให้คะแนนจิตพิสัย ===== */}
      <Modal
        title={rating ? `ให้คะแนนจิตพิสัย — ${rating.userName}` : ""}
        open={!!rating}
        onOk={submitRating}
        onCancel={() => setRating(null)}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={isSaving}
      >
        <div style={{ marginBottom: 12, color: "#8c8c8c" }}>
          เดือน {month.format("MM/YYYY")} · ช่วง <b>-1 ถึง +1</b> (ใส่ทศนิยมได้)
          <div style={{ fontSize: 12 }}>
            บวก = ดันเกรดขึ้น · ติดลบ = ดึงเกรดลง · 0 = ไม่ขยับ
          </div>
        </div>
        <InputNumber
          style={{ width: "100%" }}
          min={-1}
          max={1}
          step={0.1}
          value={attScore}
          onChange={setAttScore}
        />
        <TextArea
          style={{ marginTop: 12 }}
          rows={3}
          placeholder="เหตุผล / บันทึกเพิ่มเติม (ถ้ามี)"
          value={attNote}
          onChange={(e) => setAttNote(e.target.value)}
        />
      </Modal>

      <style>{`.kpi-ineligible { background: #fafafa; opacity: .65; }`}</style>
    </div>
  );
};

export default KpiReportPage;
